import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "../../i18n";

export const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#about", label: t.nav.about },
    { href: "#program", label: t.nav.program },
    { href: "#exhibitors", label: t.nav.exhibitors },
    { href: "#visit", label: t.nav.visit },
    { href: "#tickets", label: t.nav.tickets },
    { href: "#sponsors", label: t.nav.sponsors },
    { href: "#contact", label: t.nav.contact },
  ];

  return (
    <div className="fixed inset-x-0 top-0 z-50">
      <header
        data-testid="main-nav"
        className={`transition-[background-color,box-shadow] duration-500 ${
          scrolled ? "bg-cream/85 shadow-[0_1px_0_rgba(26,22,20,0.08)] backdrop-blur-xl" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-3 md:px-10">
          <a href="#top" data-testid="nav-logo" className="flex items-center gap-3">
            <img src="/images/logo-mark.png" alt="New York Panettone Festival" className="h-11 w-auto" />
            <span className="hidden font-serif text-sm font-semibold uppercase leading-tight tracking-[0.18em] sm:block">
              New York<br />Panettone Festival
            </span>
          </a>

          <nav className="hidden items-center gap-7 lg:flex">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                className="text-[13px] font-medium uppercase tracking-[0.14em] text-ink/70 transition-colors duration-300 hover:text-panettone"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3 md:gap-5">
            <div className="flex items-center gap-1 text-[12px] font-semibold tracking-[0.14em]" data-testid="lang-switch">
              <button
                data-testid="lang-en-btn"
                onClick={() => setLang("en")}
                className={`px-1.5 py-1 transition-colors duration-300 ${lang === "en" ? "text-panettone underline underline-offset-4" : "text-ink/50 hover:text-ink"}`}
              >
                EN
              </button>
              <span className="text-ink/25">/</span>
              <button
                data-testid="lang-it-btn"
                onClick={() => setLang("it")}
                className={`px-1.5 py-1 transition-colors duration-300 ${lang === "it" ? "text-panettone underline underline-offset-4" : "text-ink/50 hover:text-ink"}`}
              >
                IT
              </button>
            </div>
            <a
              href="#participate"
              data-testid="nav-cta-attend"
              className="hidden bg-panettone px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-ink md:block"
            >
              {t.nav.cta}
            </a>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setOpen(!open)}
              className="p-2 lg:hidden"
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden border-t border-ink/10 bg-cream/95 backdrop-blur-xl lg:hidden"
            >
              <div className="flex flex-col px-6 py-4">
                {links.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    data-testid={`mobile-nav-link-${l.href.slice(1)}`}
                    onClick={() => setOpen(false)}
                    className="border-b border-ink/8 py-3.5 font-serif text-xl italic text-ink/80 transition-colors duration-300 hover:text-panettone"
                  >
                    {l.label}
                  </a>
                ))}
                <a
                  href="#participate"
                  data-testid="mobile-nav-cta-attend"
                  onClick={() => setOpen(false)}
                  className="mt-4 bg-panettone px-5 py-3 text-center text-[12px] font-semibold uppercase tracking-[0.18em] text-cream"
                >
                  {t.nav.cta}
                </a>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>
    </div>
  );
};
